import { useMemo, useState } from "react";
import classNames from "classnames";
import type { ChartOfAccountsTemplatePreviewResponse } from "@common/schemas/chart-of-accounts-template-apply.schema";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import type { SelectedPreviewEntity } from "./applyCoaTemplatePreviewEntries";
import { ApplyCoaTemplatePreviewCoa } from "./ApplyCoaTemplatePreviewCoa";
import { ApplyCoaPreviewActions } from "./ApplyCoaPreviewActions";
import { ApplyCoaTemplatePreviewShell } from "./ApplyCoaTemplatePreviewShell";
import drawerStyles from "./ApplyCoaTemplateDrawer.module.scss";

interface Props {
    preview: ChartOfAccountsTemplatePreviewResponse;
    entries: SelectedPreviewEntity[];
    appliedEntityIds: ReadonlySet<number>;
    applyingEntityId: number | null;
    isApplyingBulk: boolean;
    onBack: () => void;
    onApply: (params: { entityIds: number[] }) => void;
    onClose: () => void;
}

const NO_DISABLED_ENTITIES: ReadonlySet<number> = new Set();

export const ApplyCoaTemplatePreviewStep = ({
    preview,
    entries,
    appliedEntityIds,
    applyingEntityId,
    isApplyingBulk,
    onBack,
    onApply,
    onClose,
}: Props) => {
    const [activeEntityId, setActiveEntityId] = useState<number | null>(
        entries[0]?.entityId ?? null,
    );
    const [selectedEntityIds, setSelectedEntityIds] = useState<Set<number>>(
        () => new Set(entries.map((entry) => entry.entityId)),
    );
    const previewByEntityId = useMemo(
        () =>
            new Map(
                preview.entities.map((entity) => [entity.entityId, entity]),
            ),
        [preview],
    );
    const activeEntry = entries.find(
        (entry) => entry.entityId === activeEntityId,
    );
    const activePreview =
        activeEntityId === null
            ? undefined
            : previewByEntityId.get(activeEntityId);
    const allEntityIds = entries.map((entry) => entry.entityId);
    const allApplied = entries.every((entry) =>
        appliedEntityIds.has(entry.entityId),
    );
    const selectedPendingIds = [...selectedEntityIds].filter(
        (entityId) => !appliedEntityIds.has(entityId),
    );
    const isBusy = isApplyingBulk || applyingEntityId !== null;

    return (
        <div
            className={classNames(
                drawerStyles.stepRoot,
                drawerStyles.previewStepRoot,
            )}
        >
            <div
                className={classNames(
                    drawerStyles.subtitleContainer,
                    drawerStyles.previewSubtitle,
                )}
            >
                <span>Step 2 of 2 · Review changes</span>
                <span>
                    Applying the template won't delete existing accounts. Open
                    the client's Chart of Accounts to merge, archive, or delete
                    them.
                </span>
            </div>
            <ApplyCoaTemplatePreviewShell
                entries={entries}
                activeEntityId={activeEntityId}
                selectedEntityIds={selectedEntityIds}
                disabledEntityIds={NO_DISABLED_ENTITIES}
                appliedEntityIds={appliedEntityIds}
                applyingEntityId={applyingEntityId}
                selectionMode="apply"
                emptyStateHeader="No clients selected"
                onActiveEntityChange={setActiveEntityId}
                onSelectedEntityIdsChange={(entityIds) =>
                    setSelectedEntityIds(new Set(entityIds))
                }
                preview={
                    activePreview && activeEntry ? (
                        <ApplyCoaTemplatePreviewCoa
                            key={activePreview.entityId}
                            entity={activePreview}
                            entitySummary={activeEntry}
                        />
                    ) : (
                        <EmptyState header="No preview for this client" />
                    )
                }
                footer={
                    <ApplyCoaPreviewActions
                        allApplied={allApplied}
                        allEntityIds={allEntityIds}
                        selectedPendingIds={selectedPendingIds}
                        isBusy={isBusy}
                        onApply={onApply}
                        onBack={onBack}
                        onClose={onClose}
                    />
                }
            />
        </div>
    );
};
